import type { ReferenceType, UseFloatingReturn, UseInteractionsReturn } from "@floating-ui/react";
import {
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
  useTransitionStyles,
} from "@floating-ui/react";
import { useRef } from "react";

import { useBool } from "@/hooks/useBool";

interface InputTooltipResult {
  isOpen: boolean;
  isMounted: boolean;
  arrowRef: React.RefObject<HTMLDivElement | null>;
  refs: UseFloatingReturn<ReferenceType>["refs"];
  floatingStyles: React.CSSProperties;
  placement: UseFloatingReturn<ReferenceType>["placement"];
  getReferenceProps: UseInteractionsReturn["getReferenceProps"];
  getFloatingProps: UseInteractionsReturn["getFloatingProps"];
  close: () => void;
}

export function useInputTooltip(): InputTooltipResult {
  const [isOpen, openHandlers] = useBool();
  const arrowRef = useRef<HTMLDivElement>(null);
  const { refs, floatingStyles, placement, context } = useFloating({
    open: isOpen,
    placement: "top-start",
    middleware: [offset(16), flip(), shift(), arrow({ element: arrowRef })],
    onOpenChange: openHandlers.set,
    whileElementsMounted: autoUpdate,
  });
  const focusInteraction = useFocus(context);
  const dismissInteraction = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([focusInteraction, dismissInteraction]);
  const { isMounted } = useTransitionStyles(context, {
    duration: 150,
  });

  return {
    refs,
    isMounted,
    floatingStyles,
    placement,
    isOpen,
    arrowRef,
    getReferenceProps,
    getFloatingProps,
    close: openHandlers.setFalse,
  };
}
